import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Truck, BarChart3, Package, DollarSign, Trash2, User, Users, ClipboardList, Download, Search, LayoutDashboard, Factory, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { exportToExcel } from '@/utils/excel';

import { AlertCircle, History, CheckCircle2 } from 'lucide-react';

import UserMenu from '@/components/ui/UserMenu';
import * as XLSX from 'xlsx';

const DIVISIONS = ['SDM', 'IT', 'Keuangan', 'Operasional & Pengolahan'];
const POSITIONS = ['Pimpinan', 'Kepala', 'Staff', 'Anggota'];
const ROLES = ['user', 'staff', 'admin'];

const MONTH_NAMES = [
  'Januari','Februari','Maret','April','Mei','Juni',
  'Juli','Agustus','September','Oktober','November','Desember'
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 1 + i);

const AdminDashboard = () => {
  const { user, logout, axiosInstance } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('bookings');

  // Bookings state
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);

  // Staff management state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [searchStaff, setSearchStaff] = useState('');

  // Global Alert Dialog state
  const [deleteDialog, setDeleteDialog] = useState({ 
    isOpen: false, 
    title: '', 
    desc: '', 
    action: null 
  });

  const confirmAction = (title, desc, action) => {
    setDeleteDialog({ isOpen: true, title, desc, action });
  };
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);


  // Logs & Issues state
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);

  // Processing state
  const [processings, setProcessings] = useState([]);
  const [loadingProcessings, setLoadingProcessings] = useState(false);
  const [showProcessingForm, setShowProcessingForm] = useState(false);
  const [processingForm, setProcessingForm] = useState({
    source_waste_category: 'organic',
    weight_processed: '',
    result_product: '',
    result_quantity: '',
    notes: ''
  });

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    
    // RBAC check
    const isAuthorized = user.role === 'admin' || ['SDM', 'IT', 'SDM & IT', 'Operasional', 'Pengolahan', 'Operasional & Pengolahan'].includes(user.division);
    if (!isAuthorized) {
      toast.error('Akses ditolak. Anda tidak memiliki akses ke halaman ini.');
      navigate('/dashboard');
      return;
    }

    if (user.role !== 'admin' && ['SDM', 'SDM & IT'].includes(user.division) && activeTab === 'bookings') {
      setActiveTab('attendance');
    }

    if (user.role === 'admin' || ['IT', 'Operasional', 'Pengolahan', 'Operasional & Pengolahan'].includes(user.division)) {
      fetchStats();
      fetchAllBookings();
    }
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'staff') fetchAllUsers();
    if (activeTab === 'attendance') fetchAttendanceReport();
  }, [activeTab, reportMonth, reportYear]);

  // ── Bookings ──────────────────────────────────────────────────────────────
  const fetchStats = async () => {
    try {
      const r = await axiosInstance.get('/admin/stats');
      setStats(r.data);
    } catch (e) {
      if (e.response?.status !== 401) toast.error('Gagal memuat statistik');
    }
  };


  const fetchLogs = async () => {
    try {
      setLoadingLogs(true);
      const r = await axiosInstance.get('/logs');
      setLogs(r.data);
    } catch (e) {
      // ignore silently if not authorized
    } finally {
      setLoadingLogs(false);
    }
  };

  const fetchIssues = async () => {
    try {
      setLoadingIssues(true);
      const r = await axiosInstance.get('/issues');
      setIssues(r.data);
    } catch (e) {
      // ignore
    } finally {
      setLoadingIssues(false);
    }
  };

  const handleResolveIssue = async (id) => {
    confirmAction('Tandai Selesai', 'Yakin ingin menandai kendala ini sudah terselesaikan?', async () => {
      try {
        await axiosInstance.put(`/issues/${id}/status`, { status: 'Resolved' });
        toast.success('Status kendala diperbarui');
        fetchIssues();
      } catch (e) {
        toast.error('Gagal memperbarui status kendala');
      }
    });
  };

  const fetchProcessings = async () => {
    try {
      setLoadingProcessings(true);
      const r = await axiosInstance.get('/processing');
      setProcessings(r.data);
    } catch (e) {
      toast.error('Gagal memuat data pengolahan');
    } finally {
      setLoadingProcessings(false);
    }
  };

  const handleCreateProcessing = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/processing', processingForm);
      toast.success('Data pengolahan berhasil ditambahkan');
      setShowProcessingForm(false);
      setProcessingForm({ ...processingForm, weight_processed: '', result_product: '', result_quantity: '', notes: '' });
      fetchProcessings();
    if (user.role === "admin" || user.division?.includes("IT")) fetchLogs();
    fetchIssues();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan data pengolahan');
    }
  };

  const handleDeleteProcessing = async (id) => {
    confirmAction('Hapus Data', 'Yakin ingin menghapus data pengolahan ini?', async () => {
      try {
        await axiosInstance.delete(`/processing/${id}`);
        toast.success('Data berhasil dihapus');
        fetchProcessings();
    if (user.role === "admin" || user.division?.includes("IT")) fetchLogs();
    fetchIssues();
        fetchStats();
      } catch (e) {
        toast.error('Gagal menghapus data');
      }
    });
  };

  const fetchAllBookings = async () => {
    try {
      const r = await axiosInstance.get('/admin/bookings');
      setBookings(r.data);
    } catch (e) {
      if (e.response?.status !== 401) toast.error('Gagal memuat pemesanan');
    } finally { setLoadingBookings(false); }
  };

  const updateBookingStatus = async (id, newStatus) => {
    try {
      await axiosInstance.patch(`/admin/bookings/${id}`, { status: newStatus });
      toast.success('Status berhasil diupdate');
      fetchAllBookings(); fetchStats();
    } catch (e) {
      if (e.response?.status !== 401) toast.error('Gagal mengupdate status');
    }
  };

  const handleDeleteBooking = (id) => {
    confirmAction(
      "Hapus Pemesanan",
      "Yakin ingin menghapus pemesanan ini? Tindakan ini tidak dapat dibatalkan.",
      async () => {
        try {
          await axiosInstance.delete(`/admin/bookings/${id}`);
          toast.success('Pemesanan berhasil dihapus');
          fetchAllBookings(); fetchStats();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Gagal menghapus pemesanan');
        }
      }
    );
  };

  const handleDeleteAllBookings = () => {
    confirmAction(
      "Hapus Semua Pemesanan",
      "⚠️ PERINGATAN! Anda akan menghapus SEMUA data pemesanan. Yakin ingin melanjutkan?",
      async () => {
        try {
          const res = await axiosInstance.delete('/admin/bookings');
          toast.success(res.data?.message || 'Semua pemesanan berhasil dihapus');
          fetchAllBookings(); fetchStats();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Gagal menghapus semua pemesanan');
        }
      }
    );
  };

  // ── Users / Staff ──────────────────────────────────────────────────────────
  const fetchAllUsers = async () => {
    setLoadingUsers(true);
    try {
      const r = await axiosInstance.get('/admin/users');
      setUsers(r.data);
    } catch (e) {
      toast.error('Gagal memuat data pengguna');
    } finally { setLoadingUsers(false); }
  };

  const updateUser = async (userId, field, value) => {
    setUpdatingUserId(userId);
    try {
      await axiosInstance.patch(`/admin/users/${userId}`, { [field]: value });
      toast.success('Data pengguna berhasil diperbarui');
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
    } catch (e) {
      toast.error('Gagal memperbarui data pengguna');
    } finally { setUpdatingUserId(null); }
  };

  const deleteUser = (userId) => {
    confirmAction(
      "Hapus Pengguna",
      "Apakah Anda yakin ingin menghapus pengguna ini? Tindakan ini tidak dapat dibatalkan.",
      async () => {
        try {
          await axiosInstance.delete(`/admin/users/${userId}`);
          toast.success('Pengguna berhasil dihapus');
          setUsers(prev => prev.filter(u => u.id !== userId));
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Gagal menghapus pengguna');
        }
      }
    );
  };

  // ── Attendance Report ──────────────────────────────────────────────────────
  const fetchAttendanceReport = async () => {
    setLoadingReport(true);
    try {
      const r = await axiosInstance.get(`/attendance/report?month=${reportMonth}&year=${reportYear}`);
      setAttendanceRecords(r.data);
    } catch (e) {
      toast.error('Gagal memuat laporan absensi');
    } finally { setLoadingReport(false); }
  };

  const updateAttendanceStatus = async (id, newStatus) => {
    try {
      await axiosInstance.patch(`/attendance/${id}`, { status: newStatus });
      setAttendanceRecords(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
      toast.success('Status absensi berhasil diperbarui');
    } catch (e) {
      toast.error('Gagal memperbarui status absensi');
    }
  };

  const deleteAttendance = (attId) => {
    confirmAction(
      "Hapus Laporan Absensi",
      "Hapus laporan absensi ini? Tindakan ini tidak dapat dibatalkan.",
      async () => {
        try {
          await axiosInstance.delete(`/attendance/${attId}`);
          setAttendanceRecords(prev => prev.filter(r => r.id !== attId));
          toast.success('Laporan berhasil dihapus');
        } catch (e) {
          toast.error('Gagal menghapus laporan');
        }
      }
    );
  };

  const deleteAllAttendance = () => {
    confirmAction(
      "Hapus Semua Data Absensi",
      "PERINGATAN: Apakah Anda yakin ingin menghapus SEMUA data absensi? Tindakan ini tidak dapat dibatalkan.",
      async () => {
        try {
          await axiosInstance.delete(`/attendance/report/all`);
          setAttendanceRecords([]);
          toast.success('Semua laporan absensi berhasil dihapus');
        } catch (e) {
          toast.error('Gagal menghapus semua laporan');
        }
      }
    );
  };

  const exportAttendanceToExcel = () => {
    if (attendanceRecords.length === 0) {
      toast.warning('Tidak ada data untuk diekspor');
      return;
    }
    const rows = attendanceRecords.map(item => ({
      "tgl": item.date.split('-')[2],
      "Nama": item.user_name,
      "Divisi": item.division || '-',
      "Status Kehadiran": item.status,
      "Jam": item.time || '-',
    }));

    const ws = XLSX.utils.aoa_to_sheet([]);
    XLSX.utils.sheet_add_aoa(ws, [
      ["Laporan Absensi Bulanan Karyawan"],
      ["PT. ECOngkut Lestari Nusantara"],
      ["Bulan:", MONTH_NAMES[reportMonth - 1] || reportMonth, "", "Tahun:", reportYear],
      [],
      ["tgl", "Nama", "Divisi", "Status Kehadiran", "Jam"]
    ], { origin: "A1" });

    // Merge title cells
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 4 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } },
    ];
    ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 12 }];

    XLSX.utils.sheet_add_json(ws, rows, { origin: "A6", skipHeader: true });

    // Tambahkan Data Validation (Dropdown) untuk kolom Status Kehadiran (Kolom D) jika didukung
    ws['!dataValidation'] = [
      {
        sqref: `D6:D${6 + rows.length + 50}`,
        type: 'list',
        allowBlank: true,
        showDropDown: true,
        formula1: '"Hadir,Izin,Sakit,Terlambat,Alpha"'
      }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Laporan Absensi");
    XLSX.writeFile(wb, `Laporan_Absensi_${MONTH_NAMES[reportMonth - 1]}_${reportYear}.xlsx`);
    toast.success('Laporan berhasil diekspor!');
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getSortScore = (u) => {
    if (u.role === 'admin') return 1000;
    if (u.role === 'user' || !u.role) return 0;
    
    let score = 0;
    if (u.position === 'Pimpinan') score = 950;
    else if (u.position === 'Kepala' || u.position === 'Kepala Divisi') score = 900;
    else if (u.position === 'Staff') score = 800;
    else if (u.position === 'Anggota') score = 700;
    else score = 600;

    const div = (u.division || '').toLowerCase();
    if (div.includes('sdm') || div.includes('it')) score += 30;
    else if (div.includes('keuangan')) score += 20;
    else if (div.includes('operasional') || div.includes('pengolahan')) score += 10;
    
    return score;
  };

  const filteredAndSortedUsers = users.filter(u => {
    const term = searchStaff.toLowerCase();
    return (u.name?.toLowerCase().includes(term) || u.email?.toLowerCase().includes(term));
  }).sort((a, b) => getSortScore(b) - getSortScore(a));

  const formatRupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
  const getPaymentStatus = (b) => {
    if (b.status === 'completed') return { text: 'Dibayar', color: 'text-green-600' };
    return { text: b.payment_status === 'paid' ? 'Dibayar' : 'Belum Dibayar', color: b.payment_status === 'paid' ? 'text-green-600' : 'text-orange-600' };
  };
  const statusColor = { pending: 'bg-yellow-100 text-yellow-700', confirmed: 'bg-blue-100 text-blue-700', 'in-transit': 'bg-purple-100 text-purple-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700' };
  const statusText = { pending: 'Menunggu', confirmed: 'Dikonfirmasi', 'in-transit': 'Dalam Perjalanan', completed: 'Selesai', cancelled: 'Dibatalkan' };
  const attendanceStatusColor = { Hadir: 'bg-green-100 text-green-700', Terlambat: 'bg-yellow-100 text-yellow-600', Alpha: 'bg-red-100 text-red-700', Izin: 'bg-blue-100 text-blue-700', Sakit: 'bg-yellow-100 text-yellow-600' };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-100">
      {/* Nav */}
      <nav className="bg-white border-b border-green-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {user?.role !== 'admin' && user?.position !== 'Pimpinan' && (
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 bg-purple-50 hover:bg-purple-100 rounded-full text-purple-700 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-purple-900 leading-none text-sm sm:text-base">Admin</p>
                <p className="text-xs text-purple-600">Sistem & Kelola</p>
              </div>
            </div>
          </div>
          <UserMenu user={user} onLogout={logout} />
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-green-900 mb-2 truncate">Admin Dashboard</h1>
            <p className="text-sm sm:text-lg text-gray-600 truncate">Kelola pemesanan, staff, dan laporan absensi</p>
          </div>
          {(user?.role === 'admin' || ['Keuangan', 'IT'].includes(user?.division) || user?.position === 'Pimpinan') && (
            <Button onClick={() => navigate('/finance')} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto shadow-md shrink-0">
              <DollarSign className="w-5 h-5 mr-2" />
              Buka Laporan Keuangan
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (user?.role === 'admin' || user?.division !== 'IT') && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { label: 'Total Pemesanan', value: stats.total_bookings, icon: Package, color: 'blue' },
              { label: 'Menunggu Konfirmasi', value: stats.pending_bookings, icon: Package, color: 'yellow' },
              { label: 'Total Pendapatan', value: formatRupiah(stats.total_revenue), icon: DollarSign, color: 'green', allowed: user?.role === 'admin' || ['Keuangan', 'IT'].includes(user?.division) },
              { label: 'Sampah Terkumpul', value: `${stats.total_waste_collected.toFixed(1)} kg`, icon: Trash2, color: 'emerald' },
            ].filter(s => s.allowed !== false).map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="p-4 sm:p-6 bg-white rounded-2xl shadow-lg border border-green-100">
                <div className="flex items-center justify-between mb-2 sm:mb-4 gap-2">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 shrink-0 bg-${color}-100 rounded-xl flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 text-${color}-600`} />
                  </div>
                  <span className={`text-xl sm:text-2xl font-bold text-${color}-600 break-words text-right leading-tight`} title={value}>{value}</span>
                </div>
                <p className="text-sm sm:text-base text-gray-600 font-medium" title={label}>{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex pb-1 mb-6 border-b border-gray-200 space-x-1 sm:space-x-2">
          {[
            { id: 'bookings', label: 'Pemesanan', icon: ClipboardList, allowed: user?.role === 'admin' || ['IT', 'Operasional', 'Pengolahan', 'Operasional & Pengolahan'].includes(user?.division) },
            { id: 'processing', label: 'Pengolahan Sampah', icon: Factory, allowed: user?.role === 'admin' || ['IT', 'Operasional', 'Pengolahan', 'Operasional & Pengolahan'].includes(user?.division) },

            { id: 'logs', label: 'Log Aktivitas', icon: History, allowed: user?.role === 'admin' || ['IT', 'SDM & IT'].includes(user?.division) },
            { id: 'issues', label: 'Kendala Sistem', icon: AlertCircle, allowed: user?.role === 'admin' || ['IT', 'SDM & IT', 'Pimpinan'].includes(user?.position || user?.division) },

            { id: 'staff', label: 'Manajemen User', icon: Users, allowed: user?.role === 'admin' || ['SDM', 'IT', 'SDM & IT'].includes(user?.division) },
            { id: 'attendance', label: 'Laporan Absensi', icon: BarChart3, allowed: user?.role === 'admin' || ['SDM', 'IT', 'SDM & IT'].includes(user?.division) },
          ].filter(t => t.allowed).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              title={label}
              className={`flex flex-1 sm:flex-none justify-center items-center space-x-0 sm:space-x-2 px-2 sm:px-5 py-3 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-green-600 text-green-700 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-green-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-5 h-5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* ── TAB: BOOKINGS ── */}
        {activeTab === 'bookings' && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-green-900">Semua Pemesanan</h2>
              <div className="flex items-center gap-2">
                
                <Button variant="outline" onClick={() => exportToExcel(bookings, 'Laporan_Pemesanan')} className="border-blue-600 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Ekspor Excel</span>
                </Button>

                <Button variant="outline" onClick={handleDeleteAllBookings} className="border-red-600 text-red-600 hover:bg-red-50" disabled={bookings.length === 0}>
                  <Trash2 className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Hapus Semua</span>
                </Button>
                <Button variant="outline" onClick={() => { fetchAllBookings(); fetchStats(); }} className="border-green-600 text-green-700 hover:bg-green-50">
                  Refresh
                </Button>
              </div>
            </div>
            {loadingBookings ? (
              <div className="text-center py-12 text-gray-500">Memuat...</div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Belum ada pemesanan</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => {
                  const pay = getPaymentStatus(booking);
                  return (
                    <div key={booking.id} className="relative p-6 border border-gray-200 rounded-xl hover:border-green-300 hover:shadow-md transition-all">
                      <button 
                        onClick={() => handleDeleteBooking(booking.id)}
                        className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Hapus Pemesanan"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-start sm:items-center">
                        <div>
                          <h3 className="font-semibold text-lg text-green-900 truncate" title={booking.waste_type_name}>{booking.waste_type_name}</h3>
                          <p className="text-sm text-gray-600 truncate" title={booking.user_email}>{booking.user_email}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2" title={booking.pickup_address}>{booking.pickup_address}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Jadwal</p>
                          <p className="font-medium truncate">{booking.pickup_date}</p>
                          <p className="text-sm text-gray-600 truncate">{booking.pickup_time}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Berat & Harga</p>
                          <p className="font-medium truncate">{booking.estimated_weight} kg</p>
                          <p className="text-lg font-bold text-green-600 truncate">{formatRupiah(booking.estimated_price)}</p>
                          <p className={`text-xs mt-1 font-medium truncate ${pay.color}`}>{pay.text}</p>
                        </div>
                        <div className="w-full">
                          <p className="text-sm text-gray-600 mb-2">Update Status</p>
                          <Select value={booking.status} onValueChange={(v) => updateBookingStatus(booking.id, v)}>
                            <SelectTrigger className={`w-full ${statusColor[booking.status] || 'bg-gray-100'} border-0 font-medium`}>
                              <SelectValue>{statusText[booking.status] || booking.status}</SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusText).map(([val, label]) => (
                                <SelectItem key={val} value={val}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* ── TAB: PENGOLAHAN SAMPAH ── */}
        {activeTab === 'processing' && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-green-900">Pengolahan Sampah</h2>
              <div className="flex flex-wrap items-center gap-2">
                <Button onClick={() => setShowProcessingForm(true)} className="bg-green-600 hover:bg-green-700 text-white">
                  <Plus className="w-4 h-4 mr-2" /> Tambah Data
                </Button>
                <Button variant="outline" onClick={() => exportToExcel(processings, 'Laporan_Pengolahan_Sampah')} className="border-blue-600 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" /> Ekspor Excel
                </Button>
                <Button variant="outline" onClick={() => { fetchProcessings();
    if (user.role === "admin" || user.division?.includes("IT")) fetchLogs();
    fetchIssues(); fetchStats(); }} className="border-green-600 text-green-700 hover:bg-green-50">
                  Refresh
                </Button>
              </div>
            </div>

            {/* Sisa Sampah Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-700 font-medium mb-1">Total Sampah Terkumpul (Semua Kategori)</p>
                <p className="text-2xl font-bold text-green-900">{stats?.total_waste_collected?.toFixed(1) || 0} kg</p>
                <p className="text-xs text-green-600 mt-2">*Hanya dari pemesanan berstatus "Selesai"</p>
              </div>
            </div>

            {loadingProcessings ? (
              <div className="text-center py-12 text-gray-500">Memuat data pengolahan...</div>
            ) : processings.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <Factory className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Belum ada data pengolahan sampah</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="py-3 px-4 font-semibold text-gray-600">Tanggal</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Kategori Sampah</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Berat Diolah</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Produk Hasil</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Jumlah/Berat Hasil</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Dicatat Oleh</th>
                      <th className="py-3 px-4 font-semibold text-gray-600 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processings.map(proc => (
                      <tr key={proc.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4">{new Date(proc.created_at).toLocaleDateString('id-ID')}</td>
                        <td className="py-3 px-4 capitalize">{proc.source_waste_category === 'organic' ? 'Organik' : 'Non-Organik'}</td>
                        <td className="py-3 px-4 font-medium text-green-600">{proc.weight_processed} kg</td>
                        <td className="py-3 px-4">{proc.result_product}</td>
                        <td className="py-3 px-4 text-blue-600">{proc.result_quantity}</td>
                        <td className="py-3 px-4">{proc.created_by_name}</td>
                        <td className="py-3 px-4 text-right">
                          <button onClick={() => handleDeleteProcessing(proc.id)} className="text-red-500 hover:text-red-700 p-2">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {/* ── TAB: LOG AKTIVITAS ── */}
        {activeTab === 'logs' && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <h2 className="text-2xl font-bold text-green-900">Log Aktivitas Sistem</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => exportToExcel(logs, 'Log_Aktivitas')} className="border-blue-600 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" /> Ekspor Excel
                </Button>
                <Button variant="outline" onClick={fetchLogs} className="border-green-600 text-green-700 hover:bg-green-50">
                  Refresh
                </Button>
              </div>
            </div>

            {loadingLogs ? (
              <div className="text-center py-12 text-gray-500">Memuat log aktivitas...</div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Belum ada aktivitas tercatat</p>
              </div>
            ) : (
              <div className="overflow-x-auto max-h-[600px]">
                <table className="w-full text-left border-collapse">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b-2 border-gray-200">
                      <th className="py-3 px-4 font-semibold text-gray-600">Waktu</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Pengguna</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Aksi</th>
                      <th className="py-3 px-4 font-semibold text-gray-600">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => (
                      <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50 text-sm">
                        <td className="py-2 px-4 whitespace-nowrap text-gray-500">{new Date(log.created_at).toLocaleString('id-ID')}</td>
                        <td className="py-2 px-4 font-medium text-gray-700">{log.user_name}</td>
                        <td className="py-2 px-4">
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-semibold">{log.action}</span>
                        </td>
                        <td className="py-2 px-4 text-gray-600">{log.details}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: KENDALA SISTEM ── */}
        {activeTab === 'issues' && (
          <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h2 className="text-2xl font-bold text-red-900 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  Daftar Kendala Sistem
                </h2>
                <p className="text-sm text-gray-500 mt-1">Laporan error dan kendala teknis dari seluruh divisi.</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => exportToExcel(issues, 'Laporan_Kendala_Sistem')} className="border-blue-600 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 mr-2" /> Ekspor Excel
                </Button>
                <Button variant="outline" onClick={fetchIssues} className="border-red-600 text-red-700 hover:bg-red-50">
                  Refresh
                </Button>
              </div>
            </div>

            {loadingIssues ? (
              <div className="text-center py-12 text-gray-500">Memuat laporan kendala...</div>
            ) : issues.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
                <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">Sistem berjalan lancar. Tidak ada laporan kendala.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {issues.map(issue => (
                  <div key={issue.id} className={`p-5 rounded-xl border ${issue.status === 'Resolved' ? 'bg-gray-50 border-gray-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${issue.status === 'Resolved' ? 'bg-green-100 text-green-700' : 'bg-red-600 text-white'}`}>
                            {issue.status}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">{issue.issue_type}</span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${issue.priority === 'Tinggi' ? 'bg-red-100 text-red-700' : issue.priority === 'Sedang' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>
                            Prioritas: {issue.priority}
                          </span>
                          <span className="text-xs text-gray-500">{new Date(issue.created_at).toLocaleString('id-ID')}</span>
                        </div>
                        <p className="text-gray-800 text-sm mb-3">{issue.description}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <User className="w-3 h-3" /> Dilaporkan oleh: <span className="font-semibold">{issue.reporter_name}</span> {issue.reporter_division ? `(${issue.reporter_division})` : ''}
                        </div>
                      </div>
                      
                      {issue.status !== 'Resolved' && (user?.role === 'admin' || user?.division?.includes('IT')) && (
                        <div className="flex items-center">
                          <Button onClick={() => handleResolveIssue(issue.id)} className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle2 className="w-4 h-4 mr-2" /> Tandai Selesai
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}






        {/* ── TAB: USER MANAGEMENT ── */}
        {activeTab === 'staff' && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-green-900 truncate">Manajemen Staff</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">Atur role, divisi, dan posisi setiap pengguna</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                
                <Button variant="outline" onClick={() => exportToExcel(users, 'Data_Staff')} className="border-blue-600 text-blue-700 hover:bg-blue-50">
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Ekspor Excel</span>
                </Button>

                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input 
                    placeholder="Cari nama atau email..." 
                    value={searchStaff}
                    onChange={(e) => setSearchStaff(e.target.value)}
                    className="pl-9 h-9 text-sm border-gray-300 focus:border-green-500 w-full"
                  />
                </div>
                <Button variant="outline" onClick={fetchAllUsers} className="border-green-600 text-green-700 hover:bg-green-50 h-9 w-full sm:w-auto">
                  Refresh
                </Button>
              </div>
            </div>
            {loadingUsers ? (
              <div className="text-center py-12 text-gray-500">Memuat data pengguna...</div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Belum ada pengguna terdaftar</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 pb-2">
                <table className="w-full text-sm min-w-[800px]">
                  <thead>
                    <tr className="bg-green-50 text-green-800 text-left">
                      <th className="px-4 py-3 rounded-tl-lg font-semibold">Pengguna</th>
                      <th className="px-4 py-3 font-semibold">Role</th>
                      <th className="px-4 py-3 font-semibold">Divisi</th>
                      <th className="px-4 py-3 font-semibold">Posisi</th>
                      <th className="px-4 py-3 rounded-tr-lg font-semibold text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedUsers.map((u) => (
                      <tr key={u.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${updatingUserId === u.id ? 'opacity-60' : ''}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center space-x-3">
                            {u.picture ? (
                              <img src={u.picture} alt={u.name} className="w-9 h-9 rounded-full object-cover border border-gray-200" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-green-600" />
                              </div>
                            )}
                            <div>
                              <input 
                                type="text"
                                defaultValue={u.name}
                                onBlur={(e) => {
                                  if (e.target.value !== u.name && e.target.value.trim() !== '') {
                                    updateUser(u.id, 'name', e.target.value);
                                  }
                                }}
                                disabled={updatingUserId === u.id || (user?.role !== 'admin' && (u.role === 'admin' || u.position === 'Pimpinan'))}
                                className="font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-green-500 focus:outline-none focus:ring-0 px-0 max-w-[150px] transition-colors"
                                title="Klik untuk mengedit nama"
                              />
                              <p className="text-xs text-gray-500 truncate max-w-[150px]" title={u.email}>{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Select
                            value={u.role || 'user'}
                            onValueChange={(v) => updateUser(u.id, 'role', v)}
                            disabled={updatingUserId === u.id || (user?.role !== 'admin' && (u.role === 'admin' || u.position === 'Pimpinan' || !['Pimpinan', 'Kepala', 'Kepala Divisi'].includes(user?.position)))}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs border-gray-300">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ROLES.map(r => (
                                <SelectItem key={r} value={r} disabled={r === 'admin' && user?.role !== 'admin'}>
                                  {r}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-4">
                          <Select
                            value={u.division || 'Umum'}
                            onValueChange={(v) => updateUser(u.id, 'division', v)}
                            disabled={updatingUserId === u.id || (user?.role !== 'admin' && (u.role === 'admin' || u.position === 'Pimpinan'))}
                          >
                            <SelectTrigger className="w-36 h-8 text-xs border-gray-300">
                              <SelectValue placeholder="Pilih Divisi" />
                            </SelectTrigger>
                            <SelectContent>
                              {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-4">
                          <Select
                            value={u.position || 'Anggota'}
                            onValueChange={(v) => updateUser(u.id, 'position', v)}
                            disabled={updatingUserId === u.id || (user?.role !== 'admin' && (u.role === 'admin' || u.position === 'Pimpinan'))}
                          >
                            <SelectTrigger className="w-40 h-8 text-xs border-gray-300">
                              <SelectValue placeholder="Pilih Posisi" />
                            </SelectTrigger>
                            <SelectContent>
                              {POSITIONS.map(p => (
                                <SelectItem key={p} value={p} disabled={p === 'Pimpinan' && user?.role !== 'admin'}>
                                  {p}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteUser(u.id)}
                            disabled={updatingUserId === u.id || (user?.role !== 'admin' && (u.role === 'admin' || u.position === 'Pimpinan')) || user?.id === u.id}
                            className="text-red-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            title="Hapus Pengguna"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: ATTENDANCE REPORT ── */}
        {activeTab === 'attendance' && (
          <div className="bg-white rounded-2xl shadow-lg border border-green-100 p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-bold text-green-900">Laporan Absensi</h2>
                <p className="text-sm text-gray-500 mt-1">Laporan Evaluasi Bulanan PT. ECOngkut Lestari Nusantara</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  className="border border-gray-300 rounded-md p-2 text-sm focus:ring-green-500 focus:border-green-500"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
                <select
                  className="border border-gray-300 rounded-md p-2 text-sm focus:ring-green-500 focus:border-green-500"
                  value={reportYear}
                  onChange={(e) => setReportYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button onClick={deleteAllAttendance} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50 text-sm flex items-center gap-2 h-9">
                  <Trash2 className="w-4 h-4" /> Hapus Semua
                </Button>
                <Button onClick={exportAttendanceToExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-2 h-9">
                  <Download className="w-4 h-4" /> Ekspor Excel
                </Button>
              </div>
            </div>

            {/* Report Header Preview */}
            <div className="mb-6 py-8 px-4 bg-[#f0fdf4] rounded-2xl border border-green-100 text-center shadow-sm">
              <p className="font-bold text-[#064e3b] text-xl mb-1">Laporan Absensi Bulanan Karyawan</p>
              <p className="font-medium text-[#065f46] text-lg mb-3">PT. ECOngkut Lestari Nusantara</p>
              <p className="text-[#047857] text-sm font-medium">Bulan: {MONTH_NAMES[reportMonth - 1] || reportMonth} &nbsp;&nbsp;&nbsp; Tahun: {reportYear}</p>
            </div>

            {loadingReport ? (
              <div className="text-center py-12 text-gray-500">Memuat laporan...</div>
            ) : attendanceRecords.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Tidak ada data absensi untuk periode ini</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 pb-2">
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="bg-green-700 text-white text-center">
                      <th className="px-3 py-3 font-semibold text-left">Tgl</th>
                      <th className="px-3 py-3 font-semibold text-left">Nama</th>
                      <th className="px-3 py-3 font-semibold text-left">Divisi</th>
                      <th className="px-3 py-3 font-semibold text-center">Status Kehadiran</th>
                      <th className="px-3 py-3 font-semibold text-left">Jam</th>
                      <th className="px-3 py-3 font-semibold text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRecords.map((rec, i) => (
                      <tr key={rec.id} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-green-50 transition-colors`}>
                        <td className="px-3 py-3 font-mono font-semibold text-gray-700">{rec.date.split('-')[2]}</td>
                        <td className="px-3 py-3 font-medium text-gray-800">{rec.user_name}</td>
                        <td className="px-3 py-3 text-gray-600">{rec.division || '-'}</td>
                        <td className="px-3 py-4 text-center align-middle">
                          <Select value={rec.status} onValueChange={(v) => updateAttendanceStatus(rec.id, v)}>
                            <SelectTrigger className={`w-28 h-8 text-xs font-bold border-0 mx-auto ${attendanceStatusColor[rec.status] || 'bg-gray-100 text-gray-700'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['Hadir', 'Izin', 'Sakit', 'Terlambat', 'Alpha'].map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-4 text-gray-600 font-mono text-xs">{rec.time || '-'}</td>
                        <td className="px-3 py-4 text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteAttendance(rec.id)}
                            className="text-red-500 hover:bg-red-50 hover:text-red-600 w-8 h-8"
                            title="Hapus Laporan"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── GLOBAL MODALS / DIALOGS ── */}
      
      {/* Modal Form Tambah Pengolahan */}
      {showProcessingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-green-50">
              <h3 className="text-lg font-bold text-green-900 flex items-center gap-2">
                <Factory className="w-5 h-5 text-green-600" />
                Catat Pengolahan Sampah
              </h3>
            </div>
            <form onSubmit={handleCreateProcessing} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategori Sampah (Bahan Baku)</label>
                <Select value={processingForm.source_waste_category} onValueChange={(val) => setProcessingForm({...processingForm, source_waste_category: val})}>
                  <SelectTrigger><SelectValue placeholder="Pilih Kategori" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="organic">Organik</SelectItem>
                    <SelectItem value="non-organic">Non-Organik</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Berat yang Diolah (kg)</label>
                <input required type="number" step="0.1" min="0.1" className="w-full border border-gray-300 rounded-md p-2"
                  value={processingForm.weight_processed} onChange={(e) => setProcessingForm({...processingForm, weight_processed: parseFloat(e.target.value) || ''})} />
                <p className="text-xs text-gray-500 mt-1">Pastikan sisa sampah terkumpul mencukupi.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Produk Hasil Olahan</label>
                <input required type="text" placeholder="Misal: Pupuk Kompos" className="w-full border border-gray-300 rounded-md p-2"
                  value={processingForm.result_product} onChange={(e) => setProcessingForm({...processingForm, result_product: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah/Berat Hasil (beserta satuan)</label>
                <input required type="text" placeholder="Misal: 5 kg / 10 botol" className="w-full border border-gray-300 rounded-md p-2"
                  value={processingForm.result_quantity} onChange={(e) => setProcessingForm({...processingForm, result_quantity: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catatan Tambahan (Opsional)</label>
                <textarea className="w-full border border-gray-300 rounded-md p-2" rows="2"
                  value={processingForm.notes} onChange={(e) => setProcessingForm({...processingForm, notes: e.target.value})}></textarea>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowProcessingForm(false)}>Batal</Button>
                <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white">Simpan Data</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => !open && setDeleteDialog(prev => ({ ...prev, isOpen: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {deleteDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 text-base">
              {deleteDialog.desc}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (deleteDialog.action) deleteDialog.action();
                setDeleteDialog(prev => ({ ...prev, isOpen: false }));
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default AdminDashboard;